import {Button} from '@luminopia/react-toolbox/lib/button'
import {Eye, EYES, IPrescriptionTemplate} from 'core/api/prescription'
import SelectField from 'core/components/form/select-field'
import {PrescriptionTemplatesByKey} from 'core/prescription'
import {createValidator, oneOf, required} from 'core/validation'
import * as React from 'react'
import {connect, Dispatch} from 'react-redux'
import {Field, FormProps, formValueSelector, getFormSubmitErrors, reduxForm} from 'redux-form'
import {FormError} from 'study_coordinator/components/FormError'
import {
  IState,
  selectAllowedPrescriptionTemplates,
  selectSubject,
  // selectSubjectDefaultPrescriptionTemplate,
} from 'study_coordinator/store'
import * as styles from './PrescriptionForm.css'

export interface IFormFields {
  subjectIdentifier: string
  prescriptionTemplateKey: string
  amblyopicEye: Eye
  rightEyeSnellen: string
  leftEyeSnellen: string
}

interface IComponentState {
  showJson: boolean
}

interface IStateProps {
  initialValues?: Partial<IFormFields>
  selectedPrescriptionTemplate?: IPrescriptionTemplate
  selectedAmblyopicEye?: Eye
  allowedPrescriptionTemplates: IPrescriptionTemplate[]
  formError?: string
}

interface IDispatchProps {
  onMount?: () => void
}

interface IOwnProps  {
  subjectIdentifier: string
  // TODO: The redux-form type for decorated component props is missing onSubmit, so declare it here
  onSubmit: (data: IFormFields) => void
}

export interface IProps extends
  IOwnProps,
  IDispatchProps,
  IStateProps,
  FormProps<IFormFields, IOwnProps, IComponentState> {}

export class UndecoratedSubjectPrescriptionForm extends React.PureComponent<IProps, IComponentState> {
  public state: IComponentState = {
    showJson: false,
  }

  public render() {
    const {
      handleSubmit,
      // submitting,
      allowedPrescriptionTemplates,
      subjectIdentifier,
    } = this.props

    const prescriptionTemplateProps = {
      label: 'Prescription Template',
      options: [
        { label: 'Select' },
        ...allowedPrescriptionTemplates.map((template) => ({
          value: template.key,
          label: template.name,
        })),
      ],
    }
    const amblyopicEyeProps = {
      label: 'Amblyopic Eye',
      options: [
        { label: 'Select' },
        ...EYES.map((eye) => ({
          value: eye,
          label: eye,
        })),
      ],
    }
    // const rightEyeSnellenProps = {
    //   label: 'RIGHT (OD) snellen denominator',
    //   placeholder: 'e.g. 40 (for 20/40)',
    //   allowedInput: /[\d\.]/,
    // }
    // const leftEyeSnellenProps = {
    //   label: 'LEFT (OS) snellen denominator',
    //   placeholder: 'e.g. 40 (for 20/40)',
    //   allowedInput: /[\d\.]/,
    // }

    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <form
            onSubmit={handleSubmit && handleSubmit((data) => (
              this.props.onSubmit({
                ...data,
                subjectIdentifier,
              })
            ))}
          >
            <FormError>{this.props.error}</FormError>
            <Field name='prescriptionTemplateKey' type='hidden'
              component={SelectField} props={prescriptionTemplateProps} />
            <Field name='amblyopicEye' component={SelectField} props={amblyopicEyeProps} />
            <div className={styles.buttonsContainer}>
              <Button
                type='submit'
                label='Add'
                // disabled={submitting}
                raised
                primary
              />
            </div>
          </form>
        </div>
      </div>
    )
  }

  public componentDidMount() {
    if (this.props.onMount) this.props.onMount()
  }

}

// const snellenValidator = (val: string) => {
//   if (!SNELLEN_DENOMINATORS.some((denom) => `${denom}` === val)) {
//     return 'Must be a valid snellen denominator (e.g. 40)'
//   }
// }

export const prescriptionFormFactory = (name: string) => {
  const selectFormValue = formValueSelector(name)

  const mapStateToProps = (state: IState, ownProps: IOwnProps): IStateProps => {
    const subject = selectSubject(state, ownProps.subjectIdentifier)
    const amblyopicEye = subject ? subject.amblyopicEye : undefined
    // const defaultTemplate = subject && selectSubjectDefaultPrescriptionTemplate(state, subject.subjectIdentifier)
    // const prescriptionTemplateKey = defaultTemplate ? defaultTemplate.key : undefined
    const prescriptionTemplateKey = 'CAM2_CONST_CONTRAST'
    const allowedPrescriptionTemplates = selectAllowedPrescriptionTemplates(state)
    // TODO: redux-form types are incorrectly implemented. Remove the type cast after fixed
    const errors = getFormSubmitErrors(name)(state) as any
    const formError = errors && errors._error

    const initialValues = {
      amblyopicEye,
      prescriptionTemplateKey,
    }

    const selectedPrescriptionTemplateKey = selectFormValue(state, 'prescriptionTemplateKey')
    const selectedPrescriptionTemplate = PrescriptionTemplatesByKey[selectedPrescriptionTemplateKey]
    const selectedAmblyopicEye = selectFormValue(state, 'amblyopicEye')

    return {
      allowedPrescriptionTemplates,
      selectedPrescriptionTemplate,
      selectedAmblyopicEye,
      initialValues,
      formError,
    }
  }

  const mapDispatchToProps = (dispatch: Dispatch<void>): IDispatchProps => ({
  })

  const formDecorator = reduxForm<IFormFields, IProps, IComponentState>({
    form: name,
    validate: (values, props) => {
      const validator = createValidator<typeof values>({
        amblyopicEye: [
          required,
          oneOf(EYES),
          (eye, data) => {
            // const leftEyeSnellen = Number(data.leftEyeSnellen)
            // const rightEyeSnellen = Number(data.rightEyeSnellen)
            // if (eye === LEFT_EYE) {
            //   if (leftEyeSnellen <= rightEyeSnellen) {
            //     return 'The LEFT eye has greater or equal visual acuity. Are you sure this is correct?'
            //   }
            // } else if (eye === RIGHT_EYE) {
            //   if (rightEyeSnellen <= leftEyeSnellen) {
            //     return 'The RIGHT eye has greater or equal visual acuity. Are you sure this is correct?'
            //   }
            // }
            return undefined
          },
        ],
        prescriptionTemplateKey: [required, oneOf(props.allowedPrescriptionTemplates.map((t) => t.key))],
        // // rightEyeSnellen: [required, snellenValidator],
        // leftEyeSnellen: [required, snellenValidator],
      })
      return validator(values)
    },
    enableReinitialize: true,
  })

  // TODO: I'm not sure why the type of the component is not compatible. Instead of spending tons of time on this,
  // I'm going to just type this as any until we upgrade redux-form in the hopes that might solve the issue.
  const FormDecorated = formDecorator(UndecoratedSubjectPrescriptionForm as any)

  return connect<IStateProps, IDispatchProps, IOwnProps, IState>(
    mapStateToProps,
    mapDispatchToProps,
  )(FormDecorated)
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_coordinator/src/components/prescription/PrescriptionForm.tsx